/**
 * Hook para gestionar el proceso manual completo de Envía.com
 * Orquesta la consulta de pedidos, extracción de guías y procesamiento en lotes
 */
import { useState, useCallback } from 'react'
import { EnviaManualService } from '../../services/cron/enviaManualService'
import { EnviaTrackUpdateService } from '../../services/cron/enviaTrackUpdateService'
import type {
  EnviaManualProcessPhase,
  EnviaManualProcessProgress,
  EnviaManualProcessResults,
  OrderWithGuide,
  TrackUpdateResponse,
} from '../../types/cron/enviaManual'

interface UseEnviaManualProcessReturn {
  isProcessing: boolean
  currentPhase: EnviaManualProcessPhase['phase']
  progress: EnviaManualProcessProgress
  results: EnviaManualProcessResults
  error: string | null
  execute: () => Promise<void>
  reset: () => void
}

const INITIAL_PROGRESS: EnviaManualProcessProgress = {
  ordersFetched: 0,
  totalOrders: 0,
  currentPage: 0,
  totalPages: 0,
  guidesExtracted: 0,
  guidesProcessed: 0,
  totalGuides: 0,
  currentBatch: 0,
  totalBatches: 0,
}

const INITIAL_RESULTS: EnviaManualProcessResults = {
  updated: [],
  notUpdated: [],
  summary: {
    totalGuides: 0,
    updated_count: 0,
    not_delivered_count: 0,
    not_found_count: 0,
    skipped_count: 0,
    delivered_count: 0,
  },
}

/**
 * Consolidar múltiples respuestas de track update en una sola
 */
function consolidateTrackUpdateResponses(
  responses: TrackUpdateResponse[]
): TrackUpdateResponse {
  if (responses.length === 0) {
    throw new Error('No hay respuestas para consolidar')
  }

  if (responses.length === 1) {
    return responses[0]
  }

  const consolidated: TrackUpdateResponse = {
    success: true,
    message: `Procesados ${responses.length} grupo(s) de tracking numbers`,
    timestamp: new Date().toISOString(),
    tracking_numbers_queried: responses.reduce(
      (sum, r) => sum + r.tracking_numbers_queried,
      0
    ),
    batches_processed: responses.reduce(
      (sum, r) => sum + r.batches_processed,
      0
    ),
    tracking_results_count: responses.reduce(
      (sum, r) => sum + r.tracking_results_count,
      0
    ),
    delivered_count: responses.reduce((sum, r) => sum + r.delivered_count, 0),
    updated_count: responses.reduce((sum, r) => sum + r.updated_count, 0),
    skipped_count: responses.reduce((sum, r) => sum + r.skipped_count, 0),
    not_found_count: responses.reduce((sum, r) => sum + r.not_found_count, 0),
    not_delivered_count: responses.reduce(
      (sum, r) => sum + r.not_delivered_count,
      0
    ),
    details: responses.flatMap((r) => r.details),
  }

  return consolidated
}

/**
 * Consolidar resultados del procesamiento
 */
function consolidateResults(
  orders: OrderWithGuide[],
  trackUpdateResponse: TrackUpdateResponse
): EnviaManualProcessResults {
  // Crear mapa de guía -> order para búsqueda rápida
  const guideToOrderMap = new Map<string, OrderWithGuide>()
  for (const order of orders) {
    guideToOrderMap.set(order.guia, order)
  }

  const updated: EnviaManualProcessResults['updated'] = []
  const notUpdated: EnviaManualProcessResults['notUpdated'] = []

  // Procesar cada detalle de la respuesta
  for (const detail of trackUpdateResponse.details) {
    const order = guideToOrderMap.get(detail.tracking_number)

    if (!order) {
      // No debería pasar, pero por si acaso
      continue
    }

    if (detail.action === 'updated') {
      updated.push({
        guia: detail.tracking_number,
        orders_id: order.orders_id,
        id: order.id,
        store_name: order.store_name,
      })
    } else {
      notUpdated.push({
        guia: detail.tracking_number,
        orders_id: order.orders_id,
        id: order.id,
        store_name: order.store_name,
        reason: detail.reason,
        status: detail.status,
        action: detail.action,
      })
    }
  }

  return {
    updated,
    notUpdated,
    summary: {
      totalGuides: trackUpdateResponse.tracking_numbers_queried,
      updated_count: trackUpdateResponse.updated_count,
      not_delivered_count: trackUpdateResponse.not_delivered_count,
      not_found_count: trackUpdateResponse.not_found_count,
      skipped_count: trackUpdateResponse.skipped_count,
      delivered_count: trackUpdateResponse.delivered_count,
    },
  }
}

export function useEnviaManualProcess(): UseEnviaManualProcessReturn {
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentPhase, setCurrentPhase] =
    useState<EnviaManualProcessPhase['phase']>('idle')
  const [progress, setProgress] =
    useState<EnviaManualProcessProgress>(INITIAL_PROGRESS)
  const [results, setResults] =
    useState<EnviaManualProcessResults>(INITIAL_RESULTS)
  const [error, setError] = useState<string | null>(null)

  /**
   * Ejecutar el proceso completo
   */
  const execute = useCallback(async () => {
    setIsProcessing(true)
    setError(null)
    setCurrentPhase('fetching_orders')
    setProgress(INITIAL_PROGRESS)
    setResults(INITIAL_RESULTS)

    try {
      // FASE 1: Consultar pedidos y extraer guías
      const orders = await EnviaManualService.getAllOrdersWithGuides(
        (fetchProgress) => {
          setProgress((prev) => ({
            ...prev,
            currentPage: fetchProgress.currentPage,
            totalPages: fetchProgress.totalPages,
            ordersFetched: fetchProgress.ordersFetched,
            totalOrders: fetchProgress.ordersFetched, // Aproximación
          }))
        }
      )

      setProgress((prev) => ({
        ...prev,
        totalOrders: orders.length,
        guidesExtracted: orders.length,
        totalGuides: orders.length,
        totalBatches: Math.ceil(orders.length / 10),
      }))

      // Validar que haya guías para procesar
      if (orders.length === 0) {
        setCurrentPhase('completed')
        setResults({
          updated: [],
          notUpdated: [],
          summary: {
            totalGuides: 0,
            updated_count: 0,
            not_delivered_count: 0,
            not_found_count: 0,
            skipped_count: 0,
            delivered_count: 0,
          },
        })
        setIsProcessing(false)
        return
      }

      // FASE 2: Procesar guías en lotes
      setCurrentPhase('processing_guides')
      const trackingNumbers = orders.map((order) => order.guia)

      // Dividir en grupos de máximo 50 tracking numbers
      const MAX_GROUP_SIZE = 50
      const trackingGroups: string[][] = []
      for (let i = 0; i < trackingNumbers.length; i += MAX_GROUP_SIZE) {
        trackingGroups.push(trackingNumbers.slice(i, i + MAX_GROUP_SIZE))
      }

      // Calcular el total de lotes esperados (cada grupo se divide en lotes de 10 internamente)
      const calculateTotalBatches = (groups: string[][]) => {
        return groups.reduce((total, group) => {
          return total + Math.ceil(group.length / 10)
        }, 0)
      }
      const totalExpectedBatches = calculateTotalBatches(trackingGroups)

      // Procesar cada grupo de máximo 50
      const allResponses: TrackUpdateResponse[] = []
      let totalProcessedGuides = 0

      for (
        let groupIndex = 0;
        groupIndex < trackingGroups.length;
        groupIndex++
      ) {
        const group = trackingGroups[groupIndex]

        const groupResponse =
          await EnviaTrackUpdateService.processTrackingNumbers(
            group,
            (batchProgress) => {
              // Calcular el progreso global considerando todos los grupos
              // El batchProgress.currentBatch es relativo al grupo actual
              // Necesitamos calcular cuántos lotes ya procesamos de grupos anteriores
              const batchesFromPreviousGroups = trackingGroups
                .slice(0, groupIndex)
                .reduce((sum, g) => sum + Math.ceil(g.length / 10), 0)

              const globalBatchNumber =
                batchesFromPreviousGroups + batchProgress.currentBatch

              setProgress((prev) => ({
                ...prev,
                currentBatch: globalBatchNumber,
                totalBatches: totalExpectedBatches,
                guidesProcessed:
                  totalProcessedGuides + batchProgress.processedGuides,
              }))
            }
          )

        allResponses.push(groupResponse)
        totalProcessedGuides += group.length

        // Pequeño delay entre grupos para evitar sobrecarga
        if (groupIndex < trackingGroups.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500))
        }
      }

      // Consolidar todas las respuestas en una sola
      const consolidatedTrackUpdateResponse =
        consolidateTrackUpdateResponses(allResponses)

      // FASE 3: Consolidar resultados
      const consolidatedResults = consolidateResults(
        orders,
        consolidatedTrackUpdateResponse
      )

      setResults(consolidatedResults)
      setCurrentPhase('completed')
    } catch (err: any) {
      console.error('Error en proceso manual de Envía:', err)
      setError(
        err.message || 'Error desconocido durante el proceso manual de Envía'
      )
      setCurrentPhase('error')
    } finally {
      setIsProcessing(false)
    }
  }, [])

  /**
   * Resetear estado
   */
  const reset = useCallback(() => {
    setIsProcessing(false)
    setCurrentPhase('idle')
    setProgress(INITIAL_PROGRESS)
    setResults(INITIAL_RESULTS)
    setError(null)
  }, [])

  return {
    isProcessing,
    currentPhase,
    progress,
    results,
    error,
    execute,
    reset,
  }
}

